import { forwardRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import {
  getValidSize,
  getValidProgress,
  getValidAriaLabel,
  getValidAriaValueMax,
  getValidAriaValueMin,
  getProgressClasses
} from './progressIndicator.helpers.js';

const LdsProgressIndicator = forwardRef(function LdsProgressIndicator(
  {
    progress = 0,
    size = 2,
    ariaLabel = 'Processing',
    ariaValueMax = 100,
    ariaValueMin = 0,
    className,
    ...rest
  },
  ref
) {
  // Validate prop values
  const vProgress = getValidProgress(progress);
  const progressClasses = getProgressClasses(vProgress);
  const vSize = getValidSize(size);
  const vAriaLabel = getValidAriaLabel(ariaLabel);
  let vAriaValueMax = getValidAriaValueMax(ariaValueMax);
  let vAriaValueMin = getValidAriaValueMin(ariaValueMin);

  // Check that the max/min values are logical
  if ((vAriaValueMin > vAriaValueMax) || (vAriaValueMax < vAriaValueMin)) {
    // eslint-disable-next-line no-console
    console.warn(`
    LDS Warning: A Progress Indicator has illogical ariaValueMin or ariaValueMax values set. Min must be less than max; max must be less than min. To avoid an illogical condition, the default of min = 0 and max = 100 will be used.
    Input ariaValueMin: ${ariaValueMin}
    Input ariaValueMax: ${ariaValueMax}
    Please fix the illogical min/max values above when customizing these ARIA attributes to resolve this warning.
    `);
    vAriaValueMin = 0;
    vAriaValueMax = 100;
  }

  // Compute circle dimensions from size
  const radius = vSize * 20;
  const diameter = radius * 2;
  const stroke = vSize * 2;
  const innerStroke = stroke / 2;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  // Compute the stroke dash offset for the generated SVG circle.
  const strokeDashOffset = circumference - ((vProgress / 100) * circumference);

  // Compute the font size of the percent text to be proportional to the circle size
  const fontSize = vSize * 7;

  return (
    <div
      role="progressbar"
      className={cn(
        'lds-progress-indicator',
        progressClasses,
        className
      )}
      style={{ height: `${diameter}px`, width: `${diameter}px` }}
      aria-label={vAriaLabel}
      aria-valuemax={vAriaValueMax}
      aria-valuemin={vAriaValueMin}
      aria-valuenow={vProgress}
      {...rest}
      ref={ref}
    >
      <span
        className="lds-progress-indicator-text"
        style={{ fontSize: `${fontSize}px` }}
      >
        {vProgress}%
      </span>
      <svg
          height={diameter}
          width={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
        >
          <circle
            className="lds-progress-indicator-circle-base"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            strokeWidth={innerStroke}
          />
          <circle
            className="lds-progress-indicator-circle-progress"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            style={{ strokeDashoffset: `${strokeDashOffset}` }}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
        </svg>
    </div>
  );
});

LdsProgressIndicator.propTypes = {
  progress: PropTypes.number,
  size: PropTypes.number,
  ariaLabel: PropTypes.string,
  ariaValueMax: PropTypes.number,
  ariaValueMin: PropTypes.number,
  className: PropTypes.string,
};

export default LdsProgressIndicator;
